import React from 'react';
import { Space, Tag, Typography } from 'antd';
import { FileTextOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';

const { Text } = Typography;

// 作業カテゴリのラベル
const WORK_TYPE_LABELS: Record<string, string> = {
  inspection: '点検',
  repair: '修理',
  replacement: '交換',
  maintenance: 'メンテナンス',
  emergency: '緊急作業'
};

// 優先度のメタ情報
const PRIORITY_META: Record<string, { label: string; color: string }> = {
  low: { label: '低', color: 'default' },
  medium: { label: '中', color: 'blue' },
  high: { label: '高', color: 'orange' },
  urgent: { label: '緊急', color: 'red' }
};

export interface WorkOrderDisplayProps {
  workOrderTemplateId?: string;
  workOrderTemplate?: {
    id: string;
    templateName?: string | null;
    displayId?: string | null;
    workTypeCategoryId?: string | null;
    workTypeCategory?: {
      categoryLabel?: string | null;
      color?: string | null;
    } | null;
    workType?: string | null;
    priority?: string | null;
    standardHours?: number | null;
  };
  showWorkType?: boolean;
  showPriority?: boolean;
  showStandardHours?: boolean;
  showCode?: boolean;
  noLink?: boolean;
  size?: 'small' | 'default' | 'large';
  style?: React.CSSProperties;
}

/**
 * 作業指示書テンプレート情報を統一的に表示するコンポーネント
 *
 * @example
 * // 基本的な使用（リンク付き）
 * <WorkOrderDisplay workOrderTemplate={template} />
 *
 * @example
 * // 作業カテゴリと優先度付き
 * <WorkOrderDisplay workOrderTemplate={template} showWorkType showPriority />
 *
 * @example
 * // リンクなし
 * <WorkOrderDisplay workOrderTemplate={template} noLink />
 */
export function WorkOrderDisplay({
  workOrderTemplateId,
  workOrderTemplate,
  showWorkType = false,
  showPriority = false,
  showStandardHours = false,
  showCode = false,
  noLink = false,
  size = 'default',
  style
}: WorkOrderDisplayProps): JSX.Element | null {
  const navigate = useNavigate();

  // workOrderTemplateオブジェクトまたはworkOrderTemplateIdが必要
  if (!workOrderTemplate && !workOrderTemplateId) {
    return null;
  }

  const effectiveTemplate = workOrderTemplate || {
    id: workOrderTemplateId!,
    templateName: null,
    displayId: null,
    workType: null,
    priority: null,
    standardHours: null
  };

  const displayName =
    effectiveTemplate.templateName ||
    effectiveTemplate.displayId ||
    effectiveTemplate.id;

  const workTypeLabel =
    effectiveTemplate.workTypeCategory?.categoryLabel ??
    (effectiveTemplate.workType
      ? WORK_TYPE_LABELS[effectiveTemplate.workType] || effectiveTemplate.workType
      : null);
  const workTypeColor = effectiveTemplate.workTypeCategory?.color ?? null;

  const priorityInfo = effectiveTemplate.priority
    ? PRIORITY_META[effectiveTemplate.priority] || {
      label: effectiveTemplate.priority,
      color: 'default'
    }
    : null;

  const handleClick = () => {
    if (!noLink && effectiveTemplate.id) {
      navigate(`/app/work-order-templates/${effectiveTemplate.id}`);
    }
  };

  const fontSize = size === 'small' ? 12 : size === 'large' ? 16 : 14;
  const iconSize = size === 'small' ? 14 : size === 'large' ? 20 : 16;

  return (
    <Space size={8} style={{ cursor: noLink ? 'default' : 'pointer', ...style }} wrap>
      <FileTextOutlined style={{ fontSize: iconSize, color: '#722ed1' }} />
      {noLink ? (
        <Text style={{ fontSize }}>
          {displayName}
        </Text>
      ) : (
        <Text onClick={handleClick} style={{ fontSize, cursor: 'pointer' }}>
          {displayName}
        </Text>
      )}
      {showCode && effectiveTemplate.displayId && effectiveTemplate.displayId !== displayName && (
        <Text type="secondary" style={{ fontSize: fontSize - 2 }}>
          ({effectiveTemplate.displayId})
        </Text>
      )}
      {showWorkType && workTypeLabel && (
        <Tag
          color={workTypeColor ?? 'default'}
          style={{ fontSize: fontSize - 2 }}
        >
          {workTypeLabel}
        </Tag>
      )}
      {showPriority && priorityInfo && (
        <Tag color={priorityInfo.color} style={{ fontSize: fontSize - 2 }}>
          {priorityInfo.label}
        </Tag>
      )}
      {showStandardHours && effectiveTemplate.standardHours && (
        <Tag icon={<ClockCircleOutlined />} style={{ fontSize: fontSize - 2 }}>
          {effectiveTemplate.standardHours}時間
        </Tag>
      )}
    </Space>
  );
}

